import { IQuery, IQueryMediator } from 'core-ts-types';
import * as _4 from 'lodash4';
import { Comment, CommentMeta } from '../../../../comments/builders/comment_builder';
import { ICommentsDatasource } from '../../../../comments/datasources/comments_datasource';
import { CommentServant } from '../../../../comments/servants/comment_servant';
import { IAPTermsDatasource } from '../../../../global/datasources/ap_terms_datasource';
import { APProgramSlotServant } from '../../../../global/servants/ap_program_slot_servant';
import {
  IPlanFeedbackCourseGroup,
  IPlanFeedbackCourseGroupMeta,
  PlanFeedbackCourseGroup
} from '../builders/plan_feedback_course_group_builder';
import { PlanFeedbackGenericSlotBuilder } from '../builders/plan_feedback_generic_slot_builder';
import { IPlanFeedbackTerm } from '../builders/plan_feedback_term_builder';
import { IFeedbackTermAndGenericSlot, IFeedbackTransaction } from '../builders/plan_feedback_transaction_builder';
import { IPlanFeebackCourseGroupsDatasource } from '../datasources/plan_feedback_course_groups_datasource';
import { IPlanFeebackCoursesDatasource } from '../datasources/plan_feedback_courses_datasource';
import { IPlanFeedbackProgramSlotsDatasource } from '../datasources/plan_feedback_program_slots_datasource';
import { IPlanFeebackTermsDatasource } from '../datasources/plan_feedback_terms_datasource';
import { FeedbackSuggestedPlanModel } from '../models/feedback_suggested_plan_model';
import { PlanFeedbackServant } from '../servants/plan_feedback_servant';

export class APAdvisorSentFeedbackTermsQuery implements IQuery {
  private __result: any;

  constructor(
    QueryMediator: IQueryMediator,
    private ngFeedbackSuggestedPlanModel: FeedbackSuggestedPlanModel,
    private ngPlanFeebackTermsDatasource: IPlanFeebackTermsDatasource,
    private ngPlanFeebackCoursesDatasource: IPlanFeebackCoursesDatasource,
    private ngPlanFeebackCourseGroupsDatasource: IPlanFeebackCourseGroupsDatasource,
    private ngAPTermsDatasource: IAPTermsDatasource,
    private ngCommentsDatasource: ICommentsDatasource,
    private ngPlanFeedbackProgramSlotsDatasource: IPlanFeedbackProgramSlotsDatasource
  ) {
    QueryMediator.registerQuery(
      this,
      ngFeedbackSuggestedPlanModel,
      ngPlanFeebackTermsDatasource,
      ngAPTermsDatasource,
      ngPlanFeebackCoursesDatasource,
      ngPlanFeebackCourseGroupsDatasource,
      ngCommentsDatasource
    );
  }

  getTemplateChangesGroupedByTransactionType() {
    const planFeedbackProgramSlots = this.ngPlanFeedbackProgramSlotsDatasource
      .values()
      .filter((feedback) => PlanFeedbackServant.isValidFeedback(feedback));
    return PlanFeedbackServant.getTemplateChangesGroupedByTransactionType(planFeedbackProgramSlots);
  }

  run(): this {
    let feedbackTerms: Array<IPlanFeedbackTerm['term']> = _4.map(this.ngPlanFeebackTermsDatasource.read(), 'term');
    feedbackTerms = _4.filter(feedbackTerms, (term) => {
      const termsDBTerm = this.ngAPTermsDatasource.get(term.id);
      return termsDBTerm && !termsDBTerm.is_past_term;
    });
    feedbackTerms = _4.sortBy(feedbackTerms, (term: IPlanFeedbackTerm['term']) => moment(term.term_start_dt));

    const flaggedCourses = _4.keyBy(
      CommentServant.getValidCourseComments(this.ngCommentsDatasource.metasource().derived()),
      'object_id'
    );
    const feedbackCourseGroupsWithMeta = this.ngPlanFeebackCourseGroupsDatasource.metasource().derived();

    let feedbackCourses = this.ngPlanFeebackCoursesDatasource.values();
    feedbackCourses = _4.map(feedbackCourses, (course) => ({
      ...course,
      flagInfo: flaggedCourses[course.course.id]
    }));

    // There will not be any "MOVE" type suggestions here
    // as we will convert them to ADD & REMOVE using the function we're calling
    const {
      ADD: addedCourses,
      REMOVE: removedCourses,
      NONE: nonMovedCourses
    } = PlanFeedbackServant.getCategorizedAndGroupedByTermSuggestions(feedbackCourses);
    const {
      ADD: addedCourseGroups,
      REMOVE: removedCourseGroups,
      NONE: nonMovedCourseGroups
    } = PlanFeedbackServant.getCategorizedAndGroupedByTermSuggestions(feedbackCourseGroupsWithMeta);

    const termsAndGenericSlots = _4.reduce(
      feedbackTerms,
      (termGenericSlots: IFeedbackTermAndGenericSlot[], feedbackTerm) => {
        const suggestions = [
          ..._4.get(removedCourses, feedbackTerm.id, []),
          ..._4.get(removedCourseGroups, feedbackTerm.id, []),
          ..._4.get(addedCourses, feedbackTerm.id, []),
          ..._4.get(addedCourseGroups, feedbackTerm.id, []),
          ..._4.get(nonMovedCourses, feedbackTerm.id, []),
          ..._4.get(nonMovedCourseGroups, feedbackTerm.id, [])
        ].sort((prevSuggestion, currSuggestion) => prevSuggestion['program_slot'] - currSuggestion['program_slot']);

        // gets credits
        let totalMaxCredits = 0;
        let totalMinCredits = 0;
        _4.map(suggestions, (item: any) => {
          if (item._programSlot) {
            item.min_credits = item._programSlot.min_credits;
            totalMinCredits += item.min_credits;
            item.max_credits = item._programSlot.max_credits;
            totalMaxCredits += item.max_credits;
          } else if (item.course) {
            item.credits = item.course.credits;
            totalMaxCredits += item.credits;
            totalMinCredits += item.credits;
          }
          return item;
        });

        const creditsRange =
          totalMinCredits !== totalMaxCredits ? `${totalMinCredits} - ${totalMaxCredits}` : `${totalMinCredits}`;
        // if there is no suggestion inside the term, we won't show it
        if (suggestions.length > 0) {
          termGenericSlots.push({
            id: feedbackTerm.id,
            name: feedbackTerm.name,
            term_start_dt: moment(feedbackTerm.term_start_dt),
            term_end_dt: moment(feedbackTerm.term_end_dt),
            suggestions: _4.sortBy(
              suggestions.map((suggestion) => PlanFeedbackGenericSlotBuilder.build(suggestion)),
              'text'
            ),
            creditsRange
          });
        }

        return termGenericSlots;
      },
      []
    );

    this.__result = {
      termsAndGenericSlots,
      templateChanges: this.getTemplateChangesGroupedByTransactionType()
    };

    const templateChanges = _4.values(this.__result.templateChanges);
    const totalSuggestions =
      _4.sumBy(this.__result.termsAndGenericSlots, 'suggestions.length') +
      _4.sumBy(templateChanges, 'suggestions.length');

    this.__result.templateSuggestionsExist = !_4.isEmpty(templateChanges);
    this.__result.totalSuggestions = totalSuggestions;
    this.__result.suggestionsExist = totalSuggestions > 0;

    return this;
  }

  get result() {
    return this.__result;
  }
}

angular
  .module('capability-ng-academic-planner')
  .service('ngAPAdvisorSentFeedbackTermsQuery', APAdvisorSentFeedbackTermsQuery);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/suggestions/components/plan_feedback/queries/advisor_sent_feedback_terms_query.ts